'use client';

import React, { useEffect, useRef, useState } from 'react';
import Popover from './Popover.jsx';
import { containsActiveItem, matchesItem, resolveCurrentPath } from './activeItem.js';

import { useOptionalConfig, useOptionalLocalization } from '../../lib/providers/ConfigProvider.jsx';
import { useOptionalTheme } from '../../lib/providers/ThemeProvider.jsx';

/** Clicks the browser should handle itself (new tab / window / download). */
const isModifiedClick = (event) =>
  Boolean(event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1));

/**
 * Componente NavItem configurable dinámicamente
 * Soporta iconos, etiquetas, enlaces, submenús y estados
 * Puede funcionar con o sin ConfigProvider/ThemeProvider
 */
function NavItem({ 
  item, 
  level = 0, 
  sidenavState = 'open',
  onHover = null,
  onLeave = null,
  children = null,
  className = '',
  translations = null,
  hasActiveChild = false,
  parentActive = false,
  isLastChild = false,
  // Opt-in: la flecha de navegación en items hoja de nivel 0. Por defecto
  // apagada para no cambiar el aspecto de proyectos que no la pidieron.
  showNavigateIcon = false,
  // El único ítem activo, resuelto por el Sidenav (ver activeItem.js). Sin
  // Sidenav (NavItem suelto) no llega, y el ítem se resuelve por su cuenta.
  activeItem = undefined,
  // Navegación en curso (ver Sidenav.jsx): el ítem clicado y el aviso de clic.
  pendingItem = null,
  onNavigateStart = null,
  // Navegación con el router de la app (ver Sidenav.jsx). Sin estas props se
  // navega como siempre, con window.location.
  onNavigate = null,
  linkComponent = null,
  ...props 
}) {
  // Intentar usar providers si están disponibles (opcional)
  const configContext = useOptionalConfig();
  const localizationContext = useOptionalLocalization();
  const themeContext = useOptionalTheme();
  
  const [isExpanded, setIsExpanded] = useState(item.expanded || false);
  /**
   * El resaltado se DERIVA en cada render, no se guarda en estado: un
   * `useState` ignoraba los cambios del prop tras montar y dejaba el resaltado
   * pegado en una SPA que navega sin remontar.
   *
   * Y no lo decide este ítem: el Sidenav resuelve UNO solo para todo el árbol
   * (el más específico, con la ruta del router). Si cada ítem mirara la URL por
   * su cuenta, "Overview" (/coaching) se resaltaría en todas las rutas del grupo.
   */
  const isActive =
    activeItem === undefined
      ? matchesItem(item, resolveCurrentPath(null))
      : activeItem !== null && item === activeItem;
  const isPending = pendingItem !== null && item === pendingItem;
  const [showPopover, setShowPopover] = useState(false);
  const itemRef = useRef(null);

  // Función para obtener valores de configuración con fallbacks
  const getConfigValue = (path, defaultValue) => {
    if (configContext?.getConfigValue) {
      return configContext.getConfigValue(path, defaultValue);
    }
    return defaultValue;
  };

  // Función de traducción con fallbacks
  const t = (key, fallback) => {
    // 1. Usar traducciones pasadas por props
    if (translations && translations[key]) {
      return translations[key];
    }
    // 2. Usar contexto de localización si existe
    if (localizationContext?.t) {
      return localizationContext.t(key, fallback);
    }
    // 3. Fallback
    return fallback || key;
  };

  // Configuración dinámica con valores por defecto
  const config = {
    maxNestingLevel: getConfigValue('navigation.maxNestingLevel', 3),
    showIcons: getConfigValue('navigation.showIcons', true),
    enableHover: getConfigValue('navigation.enableHover', true),
    animations: getConfigValue('animations', true)
  };

  /**
   * Renderizar icono dinámico
   * Soporta:
   * - String: nombre de icono predefinido
   * - Componente React: cualquier componente (LsgIcon, lucide-react, etc)
   * - Objeto { component: ReactComponent, url: "imagen.png" } - prioriza component
   * - Objeto { url: "imagen.png" } - renderiza imagen
   * - Objeto { svg: "..." } - SVG personalizado
   */
  const renderIcon = () => {
    if (!config.showIcons || !item.icon) return null;

    // Los iconos son sólo para el nivel raíz. En las subopciones el icono
    // competía con la línea conectora y la sangría, que ya comunican la
    // jerarquía; el `item.icon` de un hijo se ignora a propósito.
    if (level > 0) return null;

    // Si es un objeto con component o url
    if (typeof item.icon === 'object' && !React.isValidElement(item.icon)) {
      // Priorizar componente sobre URL
      if (item.icon.component) {
        const IconComponent = item.icon.component;
        return (
          <div className="sidenav-item-icon sidenav-item-icon--component">
            <IconComponent />
          </div>
        );
      }

      // Si tiene URL de imagen (png, jpg, svg)
      if (item.icon.url) {
        // Construir estilos basados en size o dimensiones personalizadas
        const imageStyle = {};
        
        if (item.icon.size) {
          // Si hay size, aplicar width y height iguales
          imageStyle.width = `${item.icon.size}px`;
          imageStyle.height = `${item.icon.size}px`;
        } else {
          // Si hay width/height específicos, usarlos
          if (item.icon.width) imageStyle.width = `${item.icon.width}px`;
          if (item.icon.height) imageStyle.height = `${item.icon.height}px`;
        }
        
        // Combinar con estilos personalizados adicionales
        const finalStyle = { ...imageStyle, ...item.icon.style };
        
        return (
          <img 
            src={item.icon.url}
            alt={item.icon.alt || item.label || 'icon'}
            className="sidenav-item-icon sidenav-item-icon--image"
            style={Object.keys(finalStyle).length > 0 ? finalStyle : undefined}
          />
        );
      }

      // Si es un objeto con contenido SVG personalizado
      if (item.icon.svg) {
        return (
          <div 
            className="sidenav-item-icon sidenav-item-icon--custom"
            dangerouslySetInnerHTML={{ __html: item.icon.svg }}
          />
        );
      }
    }

    // Si es un componente React directamente
    if (React.isValidElement(item.icon)) {
      return <div className="sidenav-item-icon sidenav-item-icon--component">{item.icon}</div>;
    }

    // Si es una función (componente React como función)
    if (typeof item.icon === 'function') {
      const IconComponent = item.icon;
      return (
        <div className="sidenav-item-icon sidenav-item-icon--component">
          <IconComponent />
        </div>
      );
    }

    // Si es un string, renderizar como SVG path (iconos predefinidos)
    if (typeof item.icon === 'string') {
      // Iconos predefinidos
      const iconPaths = {
        dashboard: "M3 3h8v8H3V3zm0 10h8v8H3v-8zM13 3h8v8h-8V3zm0 10h8v8h-8v-8z",
        manage: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-5.5 15a6.5 6.5 0 0 1 13 0 9 9 0 0 1-13 0zM12 12a3 3 0 1 1 0-6 3 3 0 0 1 0 6z",
        pulse: "M20.66 8l-2.83-2.83-1.41 1.41L13 3.17 9.88 6.29 14 10.41l-1.41 1.41L9.88 9.12 7.5 11.5 4 8l-2 2 7 7 9.66-9.66z",
        hire: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
        services: "M21 15c0-4.625-3.51-8.45-8-8.95V3h-2v3.05C6.51 6.55 3 10.375 3 15h18zM2 18h20v2H2v-2z",
        help: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z",
        settings: "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z",
        menu: "M3 12h18M3 6h18M3 18h18",
        close: "M18 6L6 18M6 6l12 12",
        chevronRight: "M9 18l6-6-6-6",
        chevronDown: "M6 9l6 6 6-6"
      };

      const iconPath = iconPaths[item.icon] || iconPaths.dashboard;

      return (
        <svg 
          className="sidenav-item-icon"
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d={iconPath} />
        </svg>
      );
    }

    return null;
  };

  /**
   * Renderizar etiqueta del elemento
   */
  const renderLabel = () => {
    // En estado collapsed, no mostrar label
    if (sidenavState === 'collapsed') {
      return null;
    }

    const label = t(item.label) || item.label;

    return (
      <span className="sidenav-item-label">
        {label}
      </span>
    );
  };

  /**
   * Renderizar indicador de expansión (dropdown).
   * Chevron hacia abajo que rota 180° cuando el grupo está expandido.
   */
  const renderExpandIcon = () => {
    // No mostrar el ícono de expansión en estado collapsed
    if (!children || sidenavState === 'collapsed') return null;

    return (
      <svg 
        className={`sidenav-item-expand ${isExpanded ? 'sidenav-item-expand--expanded' : ''}`}
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    );
  };

  /**
   * Renderizar flecha de navegación.
   * Se usa en items de nivel 0 sin hijos que llevan a otra vista (p.ej. "Admin"),
   * para diferenciarlos visualmente de los grupos que expanden un submenú inline.
   */
  const renderNavigateIcon = () => {
    if (!showNavigateIcon) return null;
    if (children || sidenavState === 'collapsed') return null;
    if (level !== 0) return null;
    if (item.showNavigateIcon === false) return null;
    if (!item.href && typeof item.onClick !== 'function') return null;

    return (
      <svg
        className="sidenav-item-navigate"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M9 18l6-6-6-6" />
      </svg>
    );
  };

  /**
   * Indicador de carga mientras se navega a este ítem. Ocupa el sitio de la
   * flecha de navegación, que se oculta mientras tanto.
   */
  const renderPendingIndicator = () => {
    if (!isPending || sidenavState === 'collapsed') return null;
    return (
      <span
        className="sidenav-item-spinner"
        role="status"
        aria-label={t('navigation.loading', 'Loading')}
      />
    );
  };

  /**
   * Renderizar badge/contador
   */
  const renderBadge = () => {
    if (!item.badge || sidenavState === 'collapsed') return null;

    return (
      <span className={`sidenav-item-badge sidenav-item-badge--${item.badge.type || 'default'}`}>
        {item.badge.content}
      </span>
    );
  };

  /**
   * Manejar clic en el elemento
   * Prioridad: onClick callback > href navigation
   */
  const handleClick = (event) => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    const hasOnClick = typeof item.onClick === 'function';
    const hasHref = Boolean(item.href);
    const isLeafLink = hasHref && !hasOnClick && !children;

    // Ctrl/Cmd/Shift/Alt o clic central sobre un enlace: lo resuelve el
    // navegador (pestaña nueva, ventana, descarga). Antes se hacía
    // preventDefault siempre y esos clics no hacían nada.
    if (isLeafLink && isModifiedClick(event)) {
      return;
    }

    // Con `linkComponent` el propio enlace del router navega (Next.js Link,
    // React Router Link…): no se le quita el evento.
    const linkNavigates = isLeafLink && linkComponent && !item.external && typeof onNavigate !== 'function';

    if ((hasOnClick || hasHref) && !linkNavigates) {
      event.preventDefault();
    }
    event.stopPropagation();
    
    // Si está collapsed y tiene hijos, mostrar popover
    if (sidenavState === 'collapsed' && children) {
      setShowPopover(!showPopover);
      return;
    }
    
    // Si tiene hijos, alternar expansión (solo en estado open)
    if (children && sidenavState === 'open') {
      setIsExpanded(!isExpanded);
    }
    
    // Si navega, el Sidenav la marca al instante como seleccionada y cargando,
    // antes de que el router termine (él decide si aplica: ver
    // isNavigationTarget).
    onNavigateStart?.(item);

    // PRIORIDAD 1: el onClick propio del ítem
    if (hasOnClick) {
      item.onClick(item, event);
      return;
    }

    if (!hasHref) return;

    if (item.external) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
      return;
    }

    // PRIORIDAD 2: el router de la app
    if (typeof onNavigate === 'function') {
      onNavigate(item, event);
      return;
    }

    // PRIORIDAD 3: el linkComponent ya navega solo
    if (linkNavigates) return;

    // PRIORIDAD 4: navegación completa del navegador (comportamiento histórico)
    window.location.href = item.href;
  };

  /**
   * Manejar hover
   */
  const handleMouseEnter = (event) => {
    if (config.enableHover && onHover) {
      onHover(event, item);
    }
  };

  /**
   * Manejar salida del hover
   */
  const handleMouseLeave = (event) => {
    if (config.enableHover && onLeave) {
      onLeave(event, item);
    }
  };

  // Expandir automáticamente si tiene un hijo activo
  useEffect(() => {
    if (hasActiveChild && children && sidenavState === 'open') {
      setIsExpanded(true);
    }
  }, [hasActiveChild, children, sidenavState]);

  // Clases CSS dinámicas
  const itemClasses = [
    'sidenav-item',
    `sidenav-item--level-${level}`,
    `sidenav-item--${sidenavState}`,
    isActive ? 'sidenav-item--active' : '',
    isPending ? 'sidenav-item--pending' : '',
    hasActiveChild ? 'sidenav-item--has-active-child' : '',
    isExpanded ? 'sidenav-item--expanded' : '',
    item.disabled ? 'sidenav-item--disabled' : '',
    children ? 'sidenav-item--has-children' : '',
    config.animations ? 'sidenav-item--animated' : '',
    parentActive && !isActive ? 'sidenav-item--parent-active' : '',
    className
  ].filter(Boolean).join(' ');

  // Si el nivel excede el máximo permitido, no renderizar
  if (level > config.maxNestingLevel) {
    return null;
  }

  // Elemento como enlace o botón
  // Solo usar 'a' si tiene href y NO tiene onClick (onClick tiene prioridad)
  const isAnchor = Boolean(item.href && !item.onClick);
  const ItemElement = isAnchor
    ? (linkComponent && !item.external ? linkComponent : 'a')
    : 'button';
  const elementProps = {
    // Un linkComponent puede no reenviar refs; el ref sólo se usa para anclar
    // el popover de los ítems con hijos, que son botones.
    ...(typeof ItemElement === 'string' && { ref: itemRef }),
    ...(!isAnchor && { type: 'button' }),
    className: itemClasses,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onClick: handleClick,
    ...(isAnchor ? { 'aria-disabled': item.disabled || undefined } : { disabled: item.disabled }),
    'aria-expanded': children ? isExpanded : undefined,
    'aria-current': isActive ? 'page' : undefined,
    'data-level': level,
    'data-id': item.id,
    // Solo agregar href si NO hay onClick (onClick tiene prioridad)
    ...(item.href && !item.onClick && { href: item.href }),
    ...(item.external && !item.onClick && { target: '_blank', rel: 'noopener noreferrer' }),
    ...props
  };

  /**
   * Convertir children a items para el Popover
   */
  const getPopoverItems = () => {
    if (!children) return [];
    
    // Si children es un array de NavItems, extraer los datos
    const childrenArray = React.Children.toArray(children);
    if (childrenArray.length === 0) return [];
    
    // Buscar en la estructura de children
    const items = [];
    
    const processChild = (child) => {
      // Si es un div con clase sidenav-submenu, procesar sus children
      if (child && child.props && child.props.className && 
          child.props.className.includes('sidenav-submenu')) {
        const submenuChildren = React.Children.toArray(child.props.children);
        submenuChildren.forEach(processChild);
        return;
      }
      
      // Si es un NavItem, extraer el item
      if (child && child.props && child.props.item) {
        const childItem = child.props.item;
        // El popover muestra un solo nivel: se resalta la opción que ES o
        // CONTIENE la activa, con la misma regla que el árbol abierto.
        items.push({
          ...childItem,
          active: containsActiveItem(childItem, activeItem ?? null),
        });
      }
    };
    
    childrenArray.forEach(processChild);
    
    return items;
  };

  const handlePopoverItemClick = (popoverItem, event) => {
    onNavigateStart?.(popoverItem);
    setShowPopover(false);

    // PRIORIDAD 1: Callback onClick
    if (typeof popoverItem.onClick === 'function') {
      popoverItem.onClick(popoverItem, event);
      return;
    }

    if (!popoverItem.href) return;

    if (popoverItem.external) {
      window.open(popoverItem.href, '_blank', 'noopener,noreferrer');
    } else if (typeof onNavigate === 'function') {
      onNavigate(popoverItem, event);
    } else {
      window.location.href = popoverItem.href;
    }
  };

  /**
   * Renderizar la línea conectora en forma de codo para items anidados.
   * Usa currentColor para que el color lo defina el CSS según el tema.
   */
  const renderConnector = () => {
    if (level === 0 || sidenavState !== 'open') return null;

    return (
      <svg
        className="sidenav-item-connector"
        style={{
          left: '-12px',
          top: 0,
          width: '12px',
          height: isLastChild ? '20px' : '100%'
        }}
        aria-hidden="true"
      >
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={isLastChild ? '20' : '100%'}
          stroke="currentColor"
          strokeWidth="1"
        />
        <path
          d="M 0 20 Q 3 20, 6 20 L 12 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
        />
      </svg>
    );
  };

  return (
    <div className="sidenav-item-container">
      {renderConnector()}
      <ItemElement {...elementProps}>
        <div className="sidenav-item-content">
          {renderIcon()}
          {renderLabel()}
          {renderBadge()}
          {renderExpandIcon()}
          {isPending ? renderPendingIndicator() : renderNavigateIcon()}
        </div>
      </ItemElement>
      
      {/* Submenú normal (solo en estado open) */}
      {children && isExpanded && sidenavState === 'open' && (
        <div className={`sidenav-submenu sidenav-submenu--level-${level + 1}`}>
          {children}
        </div>
      )}
      
      {/* Popover para mostrar hijos en estado collapsed */}
      {children && sidenavState === 'collapsed' && (
        <Popover
          visible={showPopover}
          target={itemRef.current}
          items={getPopoverItems()}
          showIcons={false}
          onItemClick={handlePopoverItemClick}
          onClose={() => setShowPopover(false)}
          placement="right"
        />
      )}
    </div>
  );
}

export default NavItem;